// ─────────────────────────────────────────────────────────────────────────────
// PREVENTED PLANTING — ELIGIBLE ACRES, ADDED LAND, AND THE ROLL
//
// Kevin, 2026-09-28: *"for claims, I need you to act as a digital adjuster, you need to
// process the claim basically as if you are an adjuster and generate what you think the
// farmer is owed… With prevent plant, for example, that can mean calculating added land
// factor, looking up eligible acres, rolling acres to another crop."*
//
// ── SOURCE ───────────────────────────────────────────────────────────────────
// Prevented Planting Standards Handbook **FCIC-25370 (12-2025)**, effective for the 2026
// and succeeding crop years, read directly — not recalled, not fitted to our own data.
// https://www.rma.usda.gov/sites/default/files/2025-12/2026-25370-Prevented-Planting-Standards-Handbook.pdf
// Every rule below cites its paragraph, and the two worked examples in ¶84B(10) are
// replayed as tests: if this file cannot reproduce RMA's own arithmetic it is wrong.
//
// ⚠ NOTHING HERE IS DERIVED FROM THE CARRIER'S NUMBERS. That is the whole point — a model
// fitted to the AIP's worksheet cannot disagree with it. See claimExpected.js.
// ─────────────────────────────────────────────────────────────────────────────

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
// Acres are compared to a tenth and multiplied by a per-acre guarantee to the cent, so
// the sums run in integer hundredths. Float addition across a dozen units does not always
// agree with itself, and this output is latched against a carrier payment to the dollar.
const cents = (v) => Math.round((num(v) || 0) * 100);
const fromCents = (c) => c / 100;
const acres100 = (v) => Math.round((num(v) || 0) * 100);

// ── ¶26C(1)(a) — THE BASE ────────────────────────────────────────────────────
//
// "The maximum number of acres certified for APH purposes, or insured acres reported for
//  insurance for the crop in any 1 of the 4 most recent CYs (not including reported PP
//  acreage that was planted to a 2nd crop unless the insured meets the DC requirements
//  stated in Paragraph 43)."
//
// ⚠ IT IS THE MAXIMUM OF THE FOUR, NOT THE AVERAGE AND NOT THE MOST RECENT. Verified
// against our own book 2026-09-28: of the 847 `pp_eligibility` rows carrying a full
// four-year history, `max_acres` equals the four-year maximum on ALL 847.
//
// ⚠ AND A YEAR WITH NO ENTRY IS NOT A YEAR WITH ZERO ACRES. A null is a year we have no
// record for; reading it as 0 cannot lower a MAX, so it is harmless here — but it is
// reported, because a base built on one known year out of four is a different quality of
// answer from one built on four, and the adjuster should see which they have.
export function eligibleAcresBase(history = []) {
  const years = (history || [])
    .map((h) => ({ year: num(h.year), acres: num(h.acres) }))
    .filter((h) => h.acres != null && h.acres >= 0);
  if (!years.length) {
    return { base: null, basis: "no_history", yearsKnown: 0, from: null,
      why: "no certified or reported acreage in any of the four most recent crop years — "
         + "eligible acres cannot be determined from history (¶26C(1)(a))" };
  }
  let best = years[0];
  for (const y of years) if (y.acres > best.acres) best = y;
  return {
    base: best.acres, basis: "four_year_max", from: best.year, yearsKnown: years.length,
    why: `¶26C(1)(a): the maximum acreage certified or reported in any 1 of the 4 most `
       + `recent crop years — ${best.acres} ac in ${best.year}`
       + (years.length < 4 ? ` (only ${years.length} of 4 years on file)` : ""),
  };
}

// ── ¶26C(1)(b) and ¶26C(10) — THE ADDED LAND RATIO ───────────────────────────
//
// "The number of acres determined above for a crop may be increased by multiplying it by
//  the ratio of the total cropland acres available for planting that the insured is
//  farming in the current CY (if greater) to the total cropland acres available for
//  planting that the insured farmed in the previous year"
//
// ¶26C(10): "determined separately by crop, policy, and irrigation practice" — and by
// COUNTY, which that sentence leaves out and its own worked example states five times
// over: "the total IRR acreage the insured has in their operation for 2026 IN COUNTY ABC
// by the amount … IN COUNTY ABC for the 2025 CY", against "the highest number of acres
// … reported for corn IN COUNTY ABC". Read from the sentence alone, a grower who bought
// ground in one county would have every county's eligibility factored up by the ratio.
//
// ⚠ "(IF GREATER)" IS A ONE-WAY RATCHET. A farm that SHRANK does not lose eligibility —
// the ratio simply does not apply. Implementing it as a plain ratio would cut a grower's
// eligible acres for selling ground, which the handbook does not say and which costs
// money in the direction nobody would notice.
//
// ⚠ AND IT IS NOT AUTOMATIC. ¶26C(1)(b)(i)-(iii) require, all three:
//   (i)   PROOF the acreage was acquired by purchase, a NEW lease (not one renewed from
//         last year), release from a USDA program, inheritance/gift, or an approved WA;
//   (ii)  acquired in time to plant it for the current CY using GFPs;
//   (iii) NO cause of loss had already occurred when it was acquired.
// So this refuses without an affirmative proof flag rather than defaulting to "yes". An
// adjuster who has not seen the deed does not get the factor.
export const ACQUISITION = Object.freeze({
  BOUGHT: "bought", NEW_LEASE: "new_lease", USDA_RELEASED: "usda_released",
  INHERITED_GIFTED: "inherited_gifted", WRITTEN_AGREEMENT: "written_agreement",
});
const VALID_ACQUISITION = new Set(Object.values(ACQUISITION));

export function addedLandRatio({
  currentCropland = null, priorCropland = null,
  acquisition = null, acquiredInTimeToPlant = null, noCauseOfLossAtAcquisition = null,
} = {}) {
  const cur = num(currentCropland), prior = num(priorCropland);
  const missing = [];
  if (cur == null || cur <= 0) missing.push("total cropland available for planting this crop year");
  if (prior == null || prior <= 0) missing.push("total cropland available for planting last crop year");
  if (missing.length) {
    return { ratio: 1, applies: false, missing,
      why: "¶26C(1)(b) needs both years' total cropland available for planting; without "
         + "them the base stands unchanged" };
  }
  // The ratchet. Not an error — most farms are here.
  if (cur <= prior) {
    return { ratio: 1, applies: false, missing: [],
      why: `¶26C(1)(b) applies only when this year's cropland is GREATER — ${cur} ac against `
         + `${prior} ac last year, so the base stands unchanged` };
  }
  // ⚠ REFUSED WITHOUT PROOF, never defaulted. The handbook's three conditions are
  // conjunctive and each is a document an adjuster has to have seen.
  const unproven = [];
  if (!VALID_ACQUISITION.has(String(acquisition || "")))
    unproven.push("how the acreage was acquired (¶26C(1)(b)(i): bought, newly leased, released "
                + "from a USDA program, inherited/gifted, or under a written agreement)");
  if (acquiredInTimeToPlant !== true)
    unproven.push("that it was acquired in time to plant for this crop year using GFPs (¶26C(1)(b)(ii))");
  if (noCauseOfLossAtAcquisition !== true)
    unproven.push("that no cause of loss had occurred when it was acquired (¶26C(1)(b)(iii))");
  if (unproven.length) {
    return { ratio: 1, applies: false, unproven,
      why: "the cropland grew, but ¶26C(1)(b) requires proof before the ratio may be applied — "
         + "unconfirmed: " + unproven.join("; ") };
  }
  const ratio = cur / prior;
  return {
    ratio, applies: true, missing: [], unproven: [],
    currentCropland: cur, priorCropland: prior,
    why: `¶26C(1)(b): ${cur} ac cropland this year against ${prior} last year — the base is `
       + `increased by ${ratio.toFixed(4)}`,
  };
}

// base x ratio, per ¶26C(10) "separately by crop, policy, and irrigation practice".
export function maxEligibleAcres({ history = [], addedLand = null } = {}) {
  const b = eligibleAcresBase(history);
  if (b.base == null) return { acres: null, base: b, added: null, why: b.why };
  const a = addedLandRatio(addedLand || {});
  const acres = a.applies ? b.base * a.ratio : b.base;
  return {
    // Acres to a tenth — the grain the handbook's own examples and our acreage reports use.
    acres: Math.round(acres * 10) / 10,
    base: b, added: a,
    why: a.applies ? `${b.why}; ${a.why}` : b.why,
  };
}

// ── ¶26C(4) — WHAT THE CROP ALREADY PLANTED COMES OFF THE TOP ────────────────
//
// "Any eligible acreage determined … will be reduced by subtracting the number of acres
// of the crop (insured and uninsured) that are TIMELY AND LATE PLANTED."
//
// So eligibility is a ceiling on planted + prevented together, not on prevented alone.
// A grower with 41 eligible corn acres who planted 16 has 25 left to be prevented on.
// Skip this and every claim on a crop the grower did get some of in the ground is
// over-stated — silently, because the gross figure is the one printed on the worksheet.
//
// ⚠ IT FLOORS AT ZERO AND SAYS SO. Planted acres exceeding the four-year base is the
// ORDINARY case for an operation that has grown, not an error — that is exactly what the
// ¶26C(1)(b) added-land ratio exists to accommodate. Returning a negative would read as
// a debt; returning 0 with `plantedExceedsBase` set says "nothing left here, and the
// reason may be that the base needs the added-land ratio applied to it."
//
// ⚠ AND AN UNKNOWN PLANTED FIGURE IS NOT ZERO. `planted: null` means nobody has told us,
// and subtracting nothing would quietly hand back the gross ceiling. It returns the base
// with `netted: false` so a caller can refuse rather than pay on it.
export function remainingEligibleAcres({ base = null, plantedAcres = null } = {}) {
  const b = num(base), p = num(plantedAcres);
  if (b == null || b < 0) {
    return { acres: null, netted: false, plantedExceedsBase: false,
      why: "no eligible-acre base to reduce (¶26C(4))" };
  }
  if (p == null) {
    return { acres: b, netted: false, plantedExceedsBase: false,
      why: "¶26C(4) reduces eligible acres by the crop's own timely and late planted acres; "
         + "those are not on file, so this is the gross ceiling and not a remaining figure" };
  }
  const rem = fromCents(Math.max(0, acres100(b) - acres100(p)));
  return {
    acres: Math.round(rem * 10) / 10,
    netted: true,
    plantedExceedsBase: acres100(p) > acres100(b),
    why: `¶26C(4): ${b} eligible less ${p} planted = ${Math.round(rem * 10) / 10} remaining`,
  };
}

// ── ¶26C(9) and ¶84B — USING ANOTHER CROP'S ELIGIBLE ACRES ───────────────────
//
// When a grower reports more prevented acres than that crop has eligibility for, the
// excess is NOT lost — it is paid out of another crop's unused eligibility. This is real
// money: on Fire Creek Farms CY2026, 56.70 of 80.40 reported corn acres were paid this
// way, as soybeans and dry beans.
//
// FOUR RULES, and three of them are counter-intuitive enough that guessing gets each wrong:
//
//  ¶26C(9)(a)(i)   The acres used FIRST come from the crop whose per-acre PP payment is
//                  MOST SIMILAR (CLOSEST) to the prevented crop's — "prior to applying
//                  share". Not the cheapest, not the largest: the closest, by absolute
//                  distance. RMA's own ¶84B(10) Example 2 proves it is absolute distance
//                  and not "next lower": kidney beans at $399 take navy beans ($336, 63
//                  away) ahead of corn ($638, 239 away) AND ahead of spring wheat ($326,
//                  73 away).
//  ¶26C(9)(a)(iii) TIE-BREAK: where two candidates sit an equal amount above and below,
//                  the HIGHER one is used first.
//  ¶26C(9)(b)      THE RATE PAID IS THE LOWER OF THE TWO. If the lending crop would pay
//                  MORE, the payment is made at the PREVENTED crop's rate (b)(i); if it
//                  would pay LESS, at the LENDING crop's rate (b)(ii). So the closest crop
//                  decides WHOSE ACRES, and the lower figure decides WHAT IS PAID — two
//                  different questions that a single "pick the best crop" rule conflates.
//  ¶84B(1)(b)      The SHARE is the QUALIFYING unit's — the one prevented from planting —
//                  never the lending unit's. RMA's Example 1 makes this concrete: grain
//                  sorghum's own share is 0.750 and the rolled acres still pay at corn's
//                  1.000.
//  ¶84B(1)(a)      "The number of acres payable under that unit is not limited to the
//                  number of physical acres in that unit."
//
// Same-crop other TYPES are consumed before other crops (¶84B(10) Example 2: kidney beans
// exhaust their own type, then navy beans — another dry bean type — before spring wheat),
// which `sameCrop` expresses.
//
// ⚠⚠ THE LENDER NEED NOT BE ON THE SAME GROUND, OR IN THE SAME COUNTY, AND ASSUMING
// OTHERWISE UNDER-PAYS. ¶26C(9) reaches "another crop INSURED FOR THE CURRENT CY", and
// ¶26C(9)(d) is explicit: "The PP payment may or may not be made from crop eligibility
// that is in the same physical location as the acreage that was actually prevented from
// being planted" — its example lends from legal section 9 to section 12. Eligible acres
// are DETERMINED per county (¶26C(10)); they are SPENT across the policy. Scoping the
// roll to the county is the natural mistake, it looks conservative, and it costs the
// grower money: it is the difference between a claim that pays and one that does not on
// a farm whose prevented ground and its unused eligibility sit in different counties.
// So this function takes the candidates the CALLER assembles, and the caller must assemble
// them from the whole policy. It cannot enforce that, which is why it is written here.
export function allocatePreventedAcres({
  preventedAcres = null, preventedPerAcre = null, preventedEligible = null,
  share = null, candidates = [],
} = {}) {
  const want = num(preventedAcres), ownRate = num(preventedPerAcre);
  const ownElig = num(preventedEligible), sh = num(share);
  const missing = [];
  if (want == null || want <= 0) missing.push("reported prevent-plant acres");
  if (ownRate == null || ownRate <= 0) missing.push("the prevented crop's per-acre PP guarantee");
  if (ownElig == null || ownElig < 0) missing.push("the prevented crop's own eligible acres");
  // ⚠ Share applies LAST (¶75(1)) and is the QUALIFYING unit's (¶84B(1)(b)). Missing, the
  // payment cannot be stated — and defaulting it to 1.000 would overstate every claim on
  // shared ground.
  if (sh == null || sh <= 0) missing.push("the prevented unit's insured share");
  if (missing.length) return { available: false, missing, allocations: [], indemnity: null };

  const alloc = [];
  let remaining = acres100(want);

  // The crop's OWN eligibility is always used first and always at its own rate.
  const ownUse = Math.min(remaining, acres100(ownElig));
  if (ownUse > 0) {
    alloc.push({ crop: null, unit: null, sameCrop: true, own: true,
      acres: ownUse / 100, perAcre: ownRate, rateFrom: "prevented crop",
      why: "the crop's own eligible acres, at its own rate" });
    remaining -= ownUse;
  }

  // ¶26C(9)(a): order the lenders. Same-crop types first, then by ABSOLUTE distance from
  // the prevented crop's rate; ¶26C(9)(a)(iii) breaks an exact tie toward the HIGHER rate.
  const lenders = (candidates || [])
    .map((c) => ({
      crop: c.crop || null, unit: c.unit || null,
      sameCrop: c.sameCrop === true, otherPolicy: c.otherPolicy === true,
      elig: num(c.eligibleAcres), perAcre: num(c.perAcre),
    }))
    .filter((c) => c.elig != null && c.elig > 0 && c.perAcre != null && c.perAcre > 0)
    .sort((a, b) => {
      if (a.sameCrop !== b.sameCrop) return a.sameCrop ? -1 : 1;
      // ⚠ THE CLAIMANT'S OWN POLICY BEFORE THE FARM'S OTHER POLICIES. ¶26C(9)(a)(i) orders
      // lenders by closest rate and is silent on policies; this tie-order is an INFERENCE from
      // what the carrier paid (CY2026, NAU): KIM MAR and SULLIVAN soybeans each borrowed corn
      // on their own policy at soybean's rate although a wheat policy's rate was closer —
      // ranking by rate across policies missed both by $4,251 and $893. Other policies are
      // reached only once the own policy's spare eligibility is spent.
      if (a.otherPolicy !== b.otherPolicy) return a.otherPolicy ? 1 : -1;
      const da = Math.abs(a.perAcre - ownRate), db = Math.abs(b.perAcre - ownRate);
      if (Math.abs(da - db) > 1e-9) return da - db;
      // ¶26C(9)(a)(iii) — equal distance above and below: the HIGHER payment first.
      return b.perAcre - a.perAcre;
    });

  for (const L of lenders) {
    if (remaining <= 0) break;
    const use = Math.min(remaining, acres100(L.elig));
    if (use <= 0) continue;
    // ¶26C(9)(b): the LOWER of the two rates is what is paid.
    const payHigher = L.perAcre > ownRate;
    alloc.push({
      crop: L.crop, unit: L.unit, sameCrop: L.sameCrop, own: false,
      acres: use / 100,
      perAcre: payHigher ? ownRate : L.perAcre,
      rateFrom: payHigher ? "prevented crop" : "lending crop",
      lenderPerAcre: L.perAcre,
      why: payHigher
        ? `¶26C(9)(b)(i): ${L.crop || "the lending crop"} would pay more, so these acres pay `
          + `at the prevented crop's rate`
        : `¶26C(9)(b)(ii): ${L.crop || "the lending crop"} pays less, so these acres pay at its rate`,
    });
    remaining -= use;
  }

  // ⚠ WHAT COULD NOT BE PLACED IS REPORTED, NEVER DROPPED. Acres with no eligibility
  // anywhere are genuinely unpaid, and a total that quietly excluded them would read as
  // the whole claim.
  const unplaced = remaining / 100;

  // ¶75(1) / ¶84B(1)(b): share LAST, and it is the qualifying unit's throughout.
  const indemnity = fromCents(alloc.reduce((s, a) => s + cents(a.acres * a.perAcre * sh), 0));

  return {
    available: true, missing: [],
    allocations: alloc,
    placedAcres: +(want - unplaced).toFixed(2),
    unplacedAcres: +unplaced.toFixed(2),
    unplacedNote: unplaced > 0
      ? `${unplaced.toFixed(1)} ac could not be placed — no crop on this policy has eligible `
        + `prevent-plant acres left (¶26C(9)). These acres are NOT payable.`
      : null,
    share: sh,
    indemnity,
  };
}
